import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  Input,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { loadReviewQueue, JOB_REVIEWS_MISSING } from '../services/jobReviews.service';
import {
  formatMoney,
  varianceColor,
  type ReviewQueueRow,
} from './job-records/jobReview.model';

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value.length === 10 ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' });
}

function matches(row: ReviewQueueRow, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return [row.jobRef, row.workspace, row.customer].some((value) => value.toLowerCase().includes(needle));
}

type GroupId = 'to_complete' | 'awaiting_install' | 'completed';

const GROUPS: Array<{ id: GroupId; title: string; hint: string }> = [
  { id: 'to_complete', title: 'To complete', hint: 'Installed or delivered, but the Job Record is not complete yet. Oldest first.' },
  { id: 'awaiting_install', title: 'Awaiting install', hint: 'Booked or in progress. The Job Record is due once the job is done.' },
  { id: 'completed', title: 'Completed', hint: 'Evaluation finished. Newest first.' },
];

function customerTitle(row: ReviewQueueRow) {
  return row.customer && row.customer !== '—' ? row.customer : row.workspace;
}

const RecordRow: React.FC<{ row: ReviewQueueRow; group: GroupId; onOpen: () => void }> = ({ row, group, onOpen }) => (
  <tr onClick={onOpen}>
    <td>
      <Typography level="title-sm">
        {customerTitle(row)}
        <Typography component="span" level="body-xs" sx={{ color: 'neutral.500', fontWeight: 500, ml: 1 }}>{row.jobRef}</Typography>
      </Typography>
      {customerTitle(row) !== row.workspace && (
        <Typography level="body-xs" sx={{ color: 'neutral.500' }} noWrap>{row.workspace}</Typography>
      )}
    </td>
    <td>
      {group === 'completed' ? (
        row.legacy ? (
          <Chip size="sm" variant="outlined">Old record</Chip>
        ) : (
          <>
            <Typography level="body-sm">Completed {formatDate(row.reviewedAt)}</Typography>
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{row.reviewedBy || '—'}</Typography>
          </>
        )
      ) : group === 'to_complete' ? (
        <>
          <Typography level="body-sm">Installed {formatDate(row.installedOn)}</Typography>
          <Typography level="body-xs" sx={{ color: row.daysWaiting && row.daysWaiting > 7 ? 'danger.500' : 'neutral.500' }}>
            {row.daysWaiting != null ? `${row.daysWaiting} day${row.daysWaiting === 1 ? '' : 's'} waiting` : ''}
            {row.recordUpdatedAt ? ' · started' : ' · not started'}
          </Typography>
        </>
      ) : (
        <Typography level="body-sm">{row.installedOn ? `Booked to ${formatDate(row.installedOn)}` : 'Not booked'}</Typography>
      )}
    </td>
    <td style={{ textAlign: 'right' }}>
      {row.summary ? formatMoney(row.summary.soldExVat) : '—'}
    </td>
    <td style={{ textAlign: 'right' }}>
      {group === 'completed' && row.summary ? (
        <Chip size="sm" variant="soft" color={varianceColor(row.summary.variance)}>
          {row.summary.variance === 0
            ? 'On plan'
            : `${formatMoney(Math.abs(row.summary.variance))} ${row.summary.variance < 0 ? 'below' : 'above'}`}
        </Chip>
      ) : group === 'completed' ? '—' : (
        <Chip size="sm" variant="soft" color={group === 'to_complete' ? 'warning' : 'neutral'}>
          {group === 'to_complete' ? 'Not complete' : 'Not due yet'}
        </Chip>
      )}
    </td>
  </tr>
);

const WorkspaceJobRecordsPage: React.FC = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<Record<GroupId, ReviewQueueRow[]>>({ to_complete: [], awaiting_install: [], completed: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tableMissing, setTableMissing] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadReviewQueue()
      .then((queue) => {
        if (cancelled) return;
        setTableMissing(queue.tableMissing);
        const byWaiting = (a: ReviewQueueRow, b: ReviewQueueRow) => (b.daysWaiting ?? -1) - (a.daysWaiting ?? -1);
        setGroups({
          to_complete: [...queue.toReview, ...queue.inProgress.filter((row) => row.installDone)].sort(byWaiting),
          awaiting_install: queue.inProgress.filter((row) => !row.installDone),
          completed: queue.reviewed,
        });
      })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load Job Records'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const shown = useMemo(() => GROUPS.map((group) => ({
    ...group,
    rows: groups[group.id].filter((row) => matches(row, search)),
  })), [groups, search]);
  const total = shown.reduce((sum, group) => sum + group.rows.length, 0);

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, py: { xs: 1, md: 2 }, width: '100%', maxWidth: 1200, mx: 'auto', boxSizing: 'border-box' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} gap={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography level="h3">Job Records</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Sold versus what each job actually cost. Opening one does not change the quote or invoice.
          </Typography>
        </Box>
        <Input
          size="sm"
          placeholder="Search job ref, workspace or customer"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: { sm: 300 } }}
        />
      </Stack>

      {tableMissing && <Alert color="warning" sx={{ mb: 2 }}>{JOB_REVIEWS_MISSING}</Alert>}
      {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size="sm" /></Stack>
      ) : total === 0 ? (
        <Card variant="outlined">
          <Typography level="body-sm" sx={{ color: 'neutral.500', p: 1, textAlign: 'center' }}>
            {search.trim() ? 'No Job Records match that search.' : 'No Job Records yet.'}
          </Typography>
        </Card>
      ) : (
        <Stack gap={3}>
          {shown.filter((group) => group.rows.length > 0).map((group) => (
            <Box key={group.id}>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }} flexWrap="wrap">
                <Typography level="title-md">{group.title}</Typography>
                <Chip size="sm" variant="soft" color={group.id === 'to_complete' ? 'warning' : group.id === 'completed' ? 'success' : 'neutral'}>
                  {group.rows.length}
                </Chip>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{group.hint}</Typography>
              </Stack>
              <Card variant="outlined" sx={{ p: 0, overflow: 'auto' }}>
                <Table hoverRow sx={{ minWidth: 700, '& tbody tr': { cursor: 'pointer' }, '& th, & td': { px: 2 } }}>
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th style={{ width: 200 }}>{group.id === 'completed' ? 'Completed' : 'Install'}</th>
                      <th style={{ width: 130, textAlign: 'right' }}>Sold (ex VAT)</th>
                      <th style={{ width: 150, textAlign: 'right' }}>{group.id === 'completed' ? 'Profit vs plan' : 'Status'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((row) => (
                      <RecordRow
                        key={row.projectId}
                        row={row}
                        group={group.id}
                        onOpen={() => navigate(`/workspaces/job-records/${row.projectId}`)}
                      />
                    ))}
                  </tbody>
                </Table>
              </Card>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default WorkspaceJobRecordsPage;
